import type { ProductsTypeV3 } from 'domains/products/products.types';

import type { UpdateArticleRequestType } from 'redux-store/slices/articles/types';
import type { ArticlesType } from 'redux-store/slices/articles/types';
import type { FetchedArticle } from 'redux-store/slices/articles/types';
import type { LMSMaterial } from 'redux-store/slices/lms/types';
import type {
  GetPaymentsMethodsResponseType,
  AttachPaymentsMethodRequestType,
  AttachPaymentsMethodResponseType,
} from 'redux-store/slices/paymentsMethods/types';
import type { CreateBillResponseType } from 'redux-store/slices/paymentsMethods/types';
import type {
  ProductsType,
  CouponCheckResponseType,
  CouponCheckQueryParamsType,
} from 'redux-store/slices/products/types';
import type {
  ProfileType,
  AuthorizationRequestType,
  ResetPasswordRequestType,
  RegistrationRequestType,
  EditProfileRequestType,
} from 'redux-store/slices/profile/types';
import type {
  GetPurchasesResponseType,
  ChargePurchasesResponseType,
  ChargePurchasesRequestType,
  GetPaymentsStatusResponseType,
} from 'redux-store/slices/purchases/types';
import type { CreateBillRequestType } from 'redux-store/slices/purchases/types';
import type { GetPaymentsStatusQueryParamsType } from 'redux-store/slices/purchases/types';

import getBaseUrl, { getBaseBillingUrl } from 'utils/getBaseUrl';

type PathParamsType = Record<string, string | number>;

const apiPoints = {
  oauth: {
    path: 'dashboard_api/v1/user/oauth/',
    getPath: null,
    method: 'POST',
  },
  profile: {
    path: 'dashboard_api/v1/user/profile/',
    getPath: null,
    method: 'GET',
  },
  editProfile: {
    path: 'dashboard_api/v1/user/profile/',
    getPath: null,
    method: 'PUT',
  },
  authorization: {
    path: 'dashboard_api/v1/user/auth/',
    getPath: null,
    method: 'POST',
  },
  logout: {
    path: 'dashboard_api/v1/user/logout/',
    getPath: null,
    method: 'GET',
  },
  resetPassword: {
    path: 'dashboard_api/v1/user/password_recovery/init/',
    getPath: null,
    method: 'POST',
  },
  registration: {
    path: 'dashboard_api/v1/user/register/',
    getPath: null,
    method: 'POST',
  },
  getLMSMaterial: {
    path: '',
    getPath: (params: PathParamsType): string =>
      `dashboard_api/v1/lms/materials/${params.materialId}/`,
    method: 'GET',
  },
  getProducts: {
    path: 'dashboard_api/v1/billing/products_v2/',
    getPath: null,
    method: 'GET',
  },
  getProductsV3: {
    path: 'dashboard_api/v1/billing/products_v3/',
    getPath: null,
    method: 'GET',
  },
  createBill: {
    path: 'api/v1/purchase-async/centapp',
    getPath: null,
    method: 'POST',
    getBaseUrlApi: getBaseBillingUrl,
  },
  getPaymentsStatus: {
    path: 'api/v1/purchase-async',
    getPath: null,
    method: 'GET',
    getBaseUrlApi: getBaseBillingUrl,
  },
  couponCheck: {
    path: 'dashboard_api/v1/billing/coupons/check/',
    getPath: null,
    method: 'GET',
  },
  getPurchases: {
    path: 'api/v1/purchases/',
    getPath: null,
    method: 'GET',
    getBaseUrlApi: getBaseBillingUrl,
  },
  chargePurchase: {
    path: 'dashboard_api/v1/billing/charge/',
    getPath: null,
    method: 'POST',
    getBaseUrlApi: getBaseUrl,
  },
  getPaymentsMethods: {
    path: 'dashboard_api/v1/billing/payment_methods/',
    getPath: null,
    method: 'GET',
  },
  attachPaymentsMethod: {
    path: 'dashboard_api/v1/billing/payment_methods/attach/',
    getPath: null,
    method: 'POST',
    getBaseUrlApi: getBaseUrl,
  },
  updateArticle: {
    path: 'api/articles/update',
    getPath: (params: PathParamsType): string =>
      `api/articles/update/${params.id}/`,
    method: 'POST',
  },
  getAllArticles: {
    path: 'api/articles/get-all',
    getPath: null,
    method: 'GET',
  },
  getArticleById: {
    path: '',
    getPath: (params: PathParamsType): string =>
      `api/articles/get-by-id/${params.id}/`,
    method: 'GET',
  },
  getArticleBySlug: {
    path: '',
    getPath: (params: PathParamsType): string =>
      `api/articles/get-by-slug/${params.slug}/`,
    method: 'GET',
  },
};

export interface ApiPointsFetchParamsType {
  oauth: {
    request?: { backend?: string; access_token?: string };
    queryParams: null;
    response: ProfileType;
  };
  profile: {
    request?: null;
    queryParams: null;
    response: ProfileType;
  };
  editProfile: {
    request?: EditProfileRequestType;
    queryParams: null;
    response: ProfileType;
  };
  authorization: {
    request: AuthorizationRequestType;
    queryParams: null;
    response: ProfileType;
  };
  logout: {
    request: null;
    queryParams: null;
    response: null;
  };
  resetPassword: {
    request: ResetPasswordRequestType;
    queryParams: null;
    response: Record<never, unknown>;
  };
  registration: {
    request: RegistrationRequestType;
    queryParams: null;
    response: Record<never, unknown>;
  };
  getLMSMaterial: {
    request: null;
    queryParams: null;
    response: LMSMaterial;
  };
  getProducts: {
    request: null;
    queryParams: null;
    response: { products: ProductsType };
  };
  getProductsV3: {
    request: null;
    queryParams: {
      screen: string;
      coupon_code?: string;
    };
    response: { products: ProductsTypeV3[] };
  };
  couponCheck: {
    request: null;
    queryParams: CouponCheckQueryParamsType;
    response: CouponCheckResponseType;
  };
  getPurchases: {
    request: null;
    queryParams: null;
    response: GetPurchasesResponseType;
  };
  chargePurchase: {
    request: ChargePurchasesRequestType;
    queryParams: null;
    response: ChargePurchasesResponseType;
  };
  getPaymentsMethods: {
    request: null;
    queryParams: null;
    response: GetPaymentsMethodsResponseType;
  };
  createBill: {
    request: CreateBillRequestType;
    queryParams: null;
    response: CreateBillResponseType;
  };
  getPaymentsStatus: {
    request: null;
    queryParams: GetPaymentsStatusQueryParamsType;
    response: GetPaymentsStatusResponseType | { retry_in: number };
  };
  attachPaymentsMethod: {
    request: AttachPaymentsMethodRequestType;
    queryParams: null;
    response: AttachPaymentsMethodResponseType;
  };
  updateArticle: {
    request: UpdateArticleRequestType;
    queryParams: null;
    response: Record<string, string>;
  };
  getAllArticles: {
    request: null;
    queryParams: null;
    response: { result: ArticlesType };
  };
  getArticleById: {
    request: null;
    queryParams: null;
    response: { result: FetchedArticle };
  };
  getArticleBySlug: {
    request: null;
    queryParams: null;
    response: { result: FetchedArticle };
  };

  // reqAuthInReferralCabinetOfLitres: {
  //   request: null;
  //   queryParams: { a: string }; //пример
  //   response: { url?: string };
  // };
}

export type AvailableApiPoints = keyof typeof apiPoints;

export default apiPoints;
